import axios from "axios";

import { normalizeApiV1Base } from "../../../constants/apiEndpoints";
import { parseRateLimitError } from "../../../utils/rateLimit";
import { AUTH_ERROR } from "./authenticationConstants";
import {
  buildEmailLookupPayload,
  buildLoginPayload,
  normalizeEmail,
} from "./authenticationPayload";

/** Transport for Program and Custom Program intake authentication. */

export const INTAKE_AUTH_PATH = Object.freeze({
  emailLookup: "/treatments/intake/auth/email-lookup/",
  accountCreate: "/treatments/intake/auth/account/",
  login: "/treatments/intake/auth/login/",
  profileCompletion: "/treatments/intake/auth/profile/",
  passwordResetRequest: "/treatments/intake/auth/password-reset/request/",
  passwordResetVerify: "/treatments/intake/auth/password-reset/verify/",
  passwordResetConfirm: "/treatments/intake/auth/password-reset/confirm/",
});

const REQUEST_TIMEOUT_MS = 12000;

const LOOKUP_CACHE_TTL_MS = 30000;
const lookupCache = new Map();

const CLIENT_API_ENDPOINT_STORAGE_KEY = "welliemd_client_api_endpoint";
const CLIENT_ID_STORAGE_KEY = "welliemd_client_id";

const tenantApiBaseUrl = (apiBaseUrl) => {
  const endpoint = apiBaseUrl
    || window.localStorage.getItem(CLIENT_API_ENDPOINT_STORAGE_KEY);
  if (!endpoint) {
    throw new IntakeAuthError(AUTH_ERROR.authenticationUnavailable);
  }
  return normalizeApiV1Base(endpoint);
};

const client = (apiBaseUrl) => {
  const clientId = window.localStorage.getItem(CLIENT_ID_STORAGE_KEY);
  return axios.create({
    baseURL: tenantApiBaseUrl(apiBaseUrl),
    timeout: REQUEST_TIMEOUT_MS,
    // Login and account-create set the patient refresh cookie. Without this,
    // the browser drops Set-Cookie on the cross-origin intake response.
    withCredentials: true,
    headers: {
      "Content-Type": "application/json",
      ...(clientId ? { "X-Client-ID": clientId } : {}),
    },
  });
};

export class IntakeAuthError extends Error {
  constructor(code, {
    fieldErrors = null,
    status = null,
    userMessage = null,
  } = {}) {
    super(code);
    this.name = "IntakeAuthError";
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.status = status;
    this.userMessage = userMessage;
  }
}

const errorCode = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.code || fallback;

/** Field-level messages from the DRF serializer, without leaking raw payloads. */
const FIELD_NAME = Object.freeze({
  first_name: "firstName",
  last_name: "lastName",
  date_of_birth: "dateOfBirth",
  terms_accepted: "acknowledged",
  privacy_accepted: "acknowledged",
  telehealth_consent_accepted: "acknowledged",
});

const fieldErrors = (error) => {
  const responseData = error?.response?.data;
  const data = responseData?.details && typeof responseData.details === "object"
    ? responseData.details
    : responseData;
  if (!data || typeof data !== "object") return null;
  const entries = Object.entries(data).filter(
    ([key, value]) => key !== "error" && key !== "message" && Array.isArray(value)
  );
  if (!entries.length) return null;
  return Object.fromEntries(entries.map(([key, value]) => [
    FIELD_NAME[key] || key,
    String(value[0]),
  ]));
};

/**
 * Does an account exist for this email?
 *
 * Resolves `{ exists, accountStatus }` on success and throws `email_lookup_unavailable`
 * otherwise. It never returns `exists: false` for a failed request: doing so
 * would push existing patients into registration (NEW-08).
 */
export const lookupEmail = async (email, { signal, apiBaseUrl } = {}) => {
  const payload = buildEmailLookupPayload(email);
  const cached = lookupCache.get(payload.email);
  if (cached && Date.now() - cached.at < LOOKUP_CACHE_TTL_MS) {
    return { exists: cached.exists, accountStatus: cached.accountStatus };
  }
  try {
    const response = await client(apiBaseUrl).post(INTAKE_AUTH_PATH.emailLookup, payload, {
      signal,
    });
    const exists = Boolean(response?.data?.exists);
    const accountStatus = response?.data?.account_status
      || (exists ? "active_patient" : "new");
    lookupCache.set(payload.email, { exists, accountStatus, at: Date.now() });
    return { exists, accountStatus };
  } catch (error) {
    throw new IntakeAuthError(AUTH_ERROR.emailLookupUnavailable, {
      status: error?.response?.status || null,
    });
  }
};

export const clearLookupCache = () => {
  lookupCache.clear();
};

/** Authenticate an existing patient; returns the tenant patient JWT. */
export const loginPatient = async (credentials, { apiBaseUrl } = {}) => {
  try {
    const response = await client(apiBaseUrl).post(
      INTAKE_AUTH_PATH.login,
      buildLoginPayload(credentials)
    );
    return response.data;
  } catch (error) {
    const responseStatus = error?.response?.status || null;
    const isRateLimited = responseStatus === 429;
    const fallback = responseStatus === 401
      ? AUTH_ERROR.credentialsInvalid
      : AUTH_ERROR.authenticationUnavailable;
    throw new IntakeAuthError(
      isRateLimited ? AUTH_ERROR.rateLimited : errorCode(error, fallback),
      {
        status: responseStatus,
        userMessage: isRateLimited ? parseRateLimitError(error) : null,
      }
    );
  }
};

const passwordResetError = (error) => {
  const responseStatus = error?.response?.status || null;
  if (responseStatus === 429) {
    return new IntakeAuthError(AUTH_ERROR.rateLimited, {
      status: responseStatus,
      userMessage: parseRateLimitError(error),
    });
  }
  const code = errorCode(error, null);
  return new IntakeAuthError(
    code === AUTH_ERROR.passwordResetCodeInvalid
      ? code
      : AUTH_ERROR.passwordResetUnavailable,
    { fieldErrors: fieldErrors(error), status: responseStatus },
  );
};

/**
 * Ask for a password reset code by email (WT-102).
 *
 * The backend answers the same whether or not a code was sent, so this only
 * resolves the seconds until another code may be requested.
 */
export const requestPasswordResetCode = async (email, { apiBaseUrl } = {}) => {
  try {
    const response = await client(apiBaseUrl).post(
      INTAKE_AUTH_PATH.passwordResetRequest,
      { email: normalizeEmail(email) },
    );
    return { resendAvailableIn: Number(response?.data?.resend_available_in) || 60 };
  } catch (error) {
    throw passwordResetError(error);
  }
};

/** Check a reset code before the patient chooses a new password. */
export const verifyPasswordResetCode = async (
  { email, code },
  { apiBaseUrl } = {},
) => {
  try {
    await client(apiBaseUrl).post(INTAKE_AUTH_PATH.passwordResetVerify, {
      email: normalizeEmail(email),
      code,
    });
  } catch (error) {
    throw passwordResetError(error);
  }
};

/** Save a new password with a valid reset code. */
export const confirmPasswordReset = async (
  { email, code, newPassword },
  { apiBaseUrl } = {},
) => {
  try {
    await client(apiBaseUrl).post(INTAKE_AUTH_PATH.passwordResetConfirm, {
      email: normalizeEmail(email),
      code,
      new_password: newPassword,
    });
  } catch (error) {
    throw passwordResetError(error);
  }
};

/**
 * Create the new patient's account.
 *
 * `requestKey` is sent as an Idempotency-Key so retries cannot create two
 * Patients.
 */
export const createPatientAccount = async (
  payload,
  { requestKey, apiBaseUrl } = {},
) => {
  try {
    const response = await client(apiBaseUrl).post(
      INTAKE_AUTH_PATH.accountCreate,
      payload,
      requestKey ? { headers: { "Idempotency-Key": requestKey } } : undefined
    );
    return response.data;
  } catch (error) {
    throw new IntakeAuthError(
      errorCode(error, AUTH_ERROR.accountCreateUnavailable),
      {
        fieldErrors: fieldErrors(error),
        status: error?.response?.status || null,
      }
    );
  }
};

/** Patch only the eligibility fields the bind projection reported missing. */
export const completePatientProfile = async (
  payload,
  { accessToken, apiBaseUrl } = {},
) => {
  try {
    const response = await client(apiBaseUrl).post(
      INTAKE_AUTH_PATH.profileCompletion,
      payload,
      accessToken
        ? { headers: { Authorization: `Bearer ${accessToken}` } }
        : undefined
    );
    return response.data;
  } catch (error) {
    throw new IntakeAuthError(
      errorCode(error, AUTH_ERROR.profileIncomplete),
      {
        fieldErrors: fieldErrors(error),
        status: error?.response?.status || null,
      }
    );
  }
};

/**
 * Fetch the authenticated patient's profile from the medical API.
 * Fails safely by returning null if not present or network error occurs.
 */
export const fetchPatientProfile = async ({ accessToken, apiBaseUrl } = {}) => {
  if (!accessToken) return null;
  try {
    const response = await client(apiBaseUrl).get(
      "/medical/patients/my_profile/",
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );
    return response.data || null;
  } catch {
    console.warn("Could not fetch patient profile.");
    return null;
  }
};
